import i18n from 'helpers/i18n';
import messages from './messages';
import RoutesHelper from 'helpers/routes';
import useCurrentUser from 'hooks/useCurrentUser';
import { toastError } from 'components/Toast';
import { useState, useContext, useEffect, useCallback, useId } from 'react';
import { LearningResourcesContext } from 'pages/LearningResources/LearningResourcesContext';
import { useLocation } from 'react-router-dom';
import { ResourceType } from 'types/api/LearningResource';
import { Video } from '../types';
import { AgeLevelTag } from './AgeLevelTag';
import { ClassVersionTag } from './ClassVersionTag';

interface VideoResourceProps {
  handleClick: (id: number, type: ResourceType) => void;
  ariaRoleDescription: string;
  ariaLabel: string;
  video: Video;
  tags?: any;
  showAllResources?: boolean;
}

function VideoResource({
  handleClick,
  ariaRoleDescription,
  ariaLabel,
  video,
  tags
}: VideoResourceProps) {
  const checkboxId = useId();
  const { roles = [] } = useCurrentUser();
  const location = useLocation();
  const currentPath = location.pathname;
  const learningResourcesContext = useContext(LearningResourcesContext);

  const { recommendedVideosArray, setRecommendedVideosArray } =
    learningResourcesContext;

  const [isChecked, setIsChecked] = useState(
    recommendedVideosArray.some(item => item.id === video.id)
  );

  if (!learningResourcesContext) {
    throw new Error(
      'Learning Resources must be used within a VideoTagsProvider'
    );
  }

  const ageLevelsTags = tags?.filter((tag: any) => tag.group === 'age_group');

  const classVersionsTags = tags?.filter(
    (tag: any) => tag.group === 'class_version'
  );

  const isCheckedItem = useCallback(() => {
    return recommendedVideosArray.some(item => item.id === video.id);
  }, [recommendedVideosArray, video.id]);

  function isCoach() {
    return (
      roles.includes('lr_coach') &&
      currentPath.includes(RoutesHelper.getPath('coach-learning-resources'))
    );
  }

  useEffect(() => {
    setIsChecked(isCheckedItem());
  }, [isCheckedItem, recommendedVideosArray]);

  function handleCheckboxChange(event: React.ChangeEvent<HTMLInputElement>) {
    const shouldRecommend = event.target.checked;
    const itemFound = recommendedVideosArray.find(item => item.id === video.id);

    if (shouldRecommend) {
      if (itemFound) {
        toastError({
          description: i18n.ft(messages.error)
        });
        return;
      }

      setRecommendedVideosArray([
        ...recommendedVideosArray,
        {
          name: video.name,
          id: video.id,
          videoURI: video.videoURI,
          estimatedTime: video.estimatedTime,
          type: video.type
        }
      ]);

      setIsChecked(true);
    } else {
      setRecommendedVideosArray(
        recommendedVideosArray.filter(item => item.id !== video.id)
      );

      setIsChecked(false);
    }
  }

  function renderClassVersions() {
    return classVersionsTags.map((classVersion: any, idx: number) => (
      <ClassVersionTag
        key={idx}
        name={classVersion.name}
        description={classVersion.description}
      />
    ));
  }

  function renderAgeLevels() {
    return ageLevelsTags.map((ageLevelTag: any, idx: number) => (
      <AgeLevelTag
        key={idx}
        name={ageLevelTag.name}
        description={ageLevelTag.description}
      />
    ));
  }

  function renderCoachSection() {
    return (
      <div className="rounded-b-lg">
        <label
          className="relative px-3 py-2 flex items-center justify-end gap-2 cursor-pointer hover:bg-ds-surface-hover"
          htmlFor={checkboxId}
        >
          <span>{i18n.ft(messages.recommend)}</span>

          <input
            type="checkbox"
            id={checkboxId}
            checked={isChecked}
            onChange={handleCheckboxChange}
            className="sr-only peer"
          />

          <div className="text-xl text-action w-6 h-6 flex items-center justify-center rounded peer-focus-visible:ring-2 peer-focus-visible:ring-action">
            {isChecked ? (
              <i className="fa-solid fa-square-check" aria-hidden="true" />
            ) : (
              <i className="fa-regular fa-square" aria-hidden="true" />
            )}
          </div>
        </label>
      </div>
    );
  }

  return (
    <div
      className={`min-w-[286px] rounded-lg h-fit cursor-pointer shadow-lg ${isChecked ? 'bg-blue-50 ring-2 ring-blue-600' : 'bg-white'}`}
    >
      <div onClick={() => handleClick(video.id, video.type)}>
        <div
          className="snap-start flex flex-shrink-0  relative"
          aria-roledescription={ariaRoleDescription}
          aria-label={ariaLabel}
          tabIndex={0}
        >
          <img
            className={
              'w-full  max-w-full rounded-t-lg object-cover md:!h-[269px] lg:!h-60 2xl:!h-44'
            }
            src={
              video.resourceType === 'non_video' && video.thumbnailUrl
                ? video.thumbnailUrl
                : `https://cdnsecakmi.kaltura.com/p/966871/thumbnail/entry_id/${video.videoURI}/width/600`
            }
            alt="Thumbnail"
          />
          <div className="mt-2 ml-2 absolute top-0 left-0 bg-white text-black px-2 py-1 rounded text-xs font-semibold">
            {video.estimatedTime}
          </div>
        </div>

        <div className="m-3 mt-4 font-semibold text-black text-lg line-clamp-2 leading-6">
          {video.name}
        </div>

        {/* Tags */}
        <div className="flex items-center gap-2 px-3 pb-4 empty:hidden">
          {classVersionsTags && renderClassVersions()}
          {ageLevelsTags && renderAgeLevels()}
        </div>
      </div>

      {isCoach() && renderCoachSection()}
    </div>
  );
}

export default VideoResource;
